(() => {
  'use strict';

  if (customElements.get('wnc-cart-coupon')) return;

  const state = { pending: false, notice: null, open: null, value: null, surface: null, controls: [] };
  const key = code => String(code || '').trim().toLowerCase();
  const preserveApplication = entry => entry.applicable === true || /^wnc-kit/i.test(entry.code) && entry.applicable !== false;
  const route = path => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/?$/, '/')}${path}`;
  };
  const getCodes = cart => {
    const codes = new Map();
    for (const entry of cart?.discount_codes || []) {
      const code = typeof entry === 'string' ? entry.trim() : String(entry?.code || '').trim();
      if (code && !codes.has(key(code))) codes.set(key(code), { code, applicable: typeof entry === 'string' ? undefined : entry.applicable });
    }
    return [...codes.values()];
  };
  const sameCodes = (cart, expected) => {
    const actual = new Map(getCodes(cart).map(entry => [key(entry.code), entry]));
    return actual.size === expected.length && expected.every(entry => actual.has(key(entry.code)) && (!preserveApplication(entry) || actual.get(key(entry.code)).applicable === true));
  };
  const sameContents = (before, after) => {
    const quantities = cart => {
      const items = new Map();
      for (const item of cart.items) {
        const properties = Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right));
        const identity = JSON.stringify([String(item.variant_id ?? item.id), item.selling_plan_allocation?.selling_plan?.id ?? null, properties]);
        items.set(identity, (items.get(identity) || 0) + Number(item.quantity));
      }
      return items;
    };
    const previous = quantities(before);
    const current = quantities(after);
    return previous.size === current.size && [...previous].every(([identity, quantity]) => current.get(identity) === quantity);
  };
  const getSections = () => {
    const sections = new Map();
    for (const cart of document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items')) {
      if (typeof cart.getSectionsToRender !== 'function') continue;
      for (const section of cart.getSectionsToRender() || []) {
        if (section?.section && section.id && section.selector) sections.set(section.section, section);
      }
    }
    return [...sections.values()].slice(0, 5);
  };
  const request = async (path, payload) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(route(path), {
        method: payload ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
        ...(payload ? { body: JSON.stringify(payload) } : {}),
        signal: controller.signal
      });
      const cart = await response.json();
      if (!response.ok || cart?.status >= 400 || cart?.errors || !Array.isArray(cart?.items) || !Number.isFinite(cart.total_price)) throw new Error('cart_request_failed');
      return cart;
    } finally {
      clearTimeout(timeout);
    }
  };
  const updateCodes = (codes, sections) => request('cart/update.js', {
    discount: codes.map(entry => entry.code).join(','),
    ...(sections.length ? { sections: sections.map(section => section.section), sections_url: window.location.pathname } : {})
  });
  const restoreCodes = async (codes, sections) => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const cart = await updateCodes(codes, sections);
        if (sameCodes(cart, codes)) return cart;
      } catch (_) {}
    }
    try {
      const cart = await request('cart.js');
      if (sameCodes(cart, codes)) return cart;
    } catch (_) {}
    throw new Error('cart_restore_unconfirmed');
  };
  const renderCart = (cart, sections) => {
    if (!sections.length) throw new Error('cart_sections_unavailable');
    const replacements = [];
    for (const section of sections) {
      const html = cart.sections?.[section.section];
      if (typeof html !== 'string') throw new Error('cart_section_unavailable');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source = parsed.querySelector(section.selector) || parsed.querySelector(section.id);
      const target = document.querySelector(section.selector) || document.querySelector(section.id);
      if (!source || !target) throw new Error('cart_section_target_unavailable');
      replacements.push({ target, html: source.innerHTML });
    }
    for (const replacement of replacements) replacement.target.innerHTML = replacement.html;
    if (typeof updateCartCounters === 'function') updateCartCounters();
    if (typeof updateFreeShipping === 'function') updateFreeShipping();
    document.dispatchEvent(new CustomEvent('wnc:cart-discount-updated', { detail: { cart } }));
  };
  const lockCart = () => {
    const selector = '[name="checkout"], [name="updates[]"], .quantity__button, cart-remove-button, .cart__dynamic-checkout';
    const known = new Set(state.controls.map(record => record.node));
    for (const node of document.querySelectorAll(selector)) {
      if (known.has(node)) continue;
      state.controls.push({ node, disabled: node.disabled, inert: node.hasAttribute('inert') });
      if ('disabled' in node) node.disabled = true;
      else node.setAttribute('inert', '');
    }
  };
  const unlockCart = () => {
    for (const { node, disabled, inert } of state.controls) {
      if ('disabled' in node) node.disabled = disabled;
      if (!inert) node.removeAttribute('inert');
    }
    state.controls = [];
  };
  const sync = () => {
    for (const component of document.querySelectorAll('wnc-cart-coupon')) component.renderState();
  };

  class WncCartCoupon extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onToggle = this.onToggle.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('input', this.onInput);
      this.addEventListener('toggle', this.onToggle, true);
      this.renderState();
      if (state.pending) lockCart();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('toggle', this.onToggle, true);
    }

    onClick(event) {
      if (!event.target.closest('[data-coupon-apply]')) return;
      event.preventDefault();
      event.stopPropagation();
      this.apply();
    }

    onKeydown(event) {
      if (event.key !== 'Enter' || !event.target.matches('[data-coupon-input]')) return;
      event.preventDefault();
      event.stopPropagation();
      this.apply();
    }

    onInput(event) {
      if (!event.target.matches('[data-coupon-input]') || state.pending) return;
      state.notice = null;
      state.value = null;
      sync();
    }

    onToggle(event) {
      if (event.target.matches('[data-coupon-details]')) state.open = event.target.open;
    }

    renderState() {
      const details = this.querySelector('[data-coupon-details]');
      const input = this.querySelector('[data-coupon-input]');
      const apply = this.querySelector('[data-coupon-apply]');
      const message = this.querySelector('[data-coupon-message]');
      const refresh = this.querySelector('[data-coupon-refresh]');
      if (!details || !input || !apply || !message) return;
      if (state.open !== null) details.open = state.open;
      if (state.value !== null) input.value = state.value;
      input.disabled = state.pending;
      apply.disabled = state.pending;
      apply.textContent = state.pending ? this.dataset.labelLoading : this.dataset.labelApply;
      this.setAttribute('aria-busy', String(state.pending));
      message.hidden = !state.notice;
      message.textContent = state.notice ? this.dataset[`message${state.notice.key[0].toUpperCase()}${state.notice.key.slice(1)}`] || '' : '';
      message.dataset.tone = state.notice?.tone || '';
      input.setAttribute('aria-invalid', String(Boolean(state.notice?.invalid)));
      if (refresh) refresh.hidden = state.notice?.key !== 'unconfirmed';
    }

    async apply() {
      if (state.pending) return;
      const input = this.querySelector('[data-coupon-input]');
      const code = input?.value.trim() || '';
      state.surface = this.dataset.surface;
      state.open = true;
      state.value = code;
      if (!code || /[,\r\n]/.test(code)) {
        state.notice = { key: code ? 'single' : 'empty', tone: 'error', invalid: true };
        sync();
        input?.focus();
        return;
      }
      if (document.querySelector('cart-items.is-loading, cart-drawer-items.is-loading')) {
        state.notice = { key: 'busy', tone: 'error' };
        sync();
        return;
      }

      state.pending = true;
      state.notice = null;
      lockCart();
      sync();
      const sections = getSections();
      let previous = null;
      let attempted = false;
      let restorationAttempted = false;
      let finalCart = null;
      try {
        const before = await request('cart.js');
        previous = getCodes(before);
        if (previous.some(entry => key(entry.code) === key(code) && entry.applicable === true)) {
          state.notice = { key: 'existing', tone: 'success' };
          state.open = false;
          state.value = '';
          return;
        }
        const combined = new Map(previous.map(entry => [key(entry.code), entry]));
        if (!combined.has(key(code))) combined.set(key(code), { code });
        attempted = true;
        const after = await updateCodes([...combined.values()], sections);
        const accepted = new Map(getCodes(after).map(entry => [key(entry.code), entry]));
        const isApplied = accepted.get(key(code))?.applicable === true;
        const lostPrevious = previous.some(entry => preserveApplication(entry) && accepted.get(key(entry.code))?.applicable !== true);
        const higherTotal = sameContents(before, after) && after.total_price > before.total_price;
        if (!isApplied || lostPrevious || higherTotal) {
          restorationAttempted = true;
          finalCart = await restoreCodes(previous, sections);
          state.notice = { key: lostPrevious || higherTotal ? 'conflict' : 'invalid', tone: 'error', invalid: true };
          state.open = true;
        } else {
          finalCart = after;
          state.notice = { key: 'success', tone: 'success' };
          state.open = false;
          state.value = '';
        }
      } catch (_) {
        state.open = true;
        if (attempted && previous && !restorationAttempted) {
          try {
            finalCart = await restoreCodes(previous, sections);
            state.notice = { key: 'network', tone: 'error' };
          } catch (_) {
            state.notice = { key: 'unconfirmed', tone: 'error' };
          }
        } else if (restorationAttempted) {
          state.notice = { key: 'unconfirmed', tone: 'error' };
        } else {
          state.notice = { key: 'network', tone: 'error' };
        }
      } finally {
        try {
          if (finalCart) renderCart(finalCart, sections);
        } catch (_) {
          state.notice = { key: 'unconfirmed', tone: 'error' };
          state.open = true;
        } finally {
          state.pending = false;
          unlockCart();
          sync();
          const component = [...document.querySelectorAll('wnc-cart-coupon')].find(item => item.dataset.surface === state.surface) || this;
          component.querySelector(state.open ? '[data-coupon-input]' : '[data-coupon-summary]')?.focus({ preventScroll: true });
        }
      }
    }
  }

  customElements.define('wnc-cart-coupon', WncCartCoupon);
})();
